import React, { FunctionComponent } from 'react';
import { useBreakpoint } from '@noths/polaris-client-responsive';
import { Breakpoint } from '@noths/polaris-client-styles';
import * as R from 'ramda';

import { CheckboxGroup } from 'src/components/molecules/CheckboxGroup';
import { Filter, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { FilterOptionsProps } from '../../types';
import { FilterOptionsExpandableContent } from './FilterOptionsExpandableContent';

const getSelectedCheckboxValues = (
  selectedFilterOptions: SelectedFilterOption[],
  filterUid?: string,
) => {
  // Note: using selectedFilterOptions to store value, not active flag
  const selectedFilterOption: SelectedFilterOption | undefined = R.find<SelectedFilterOption>(
    R.propEq('filterUid', filterUid),
  )(selectedFilterOptions);

  const selectedCheckboxValuesUncast: FilterOptionValue[] =
    (selectedFilterOption?.filterOptionValue as FilterOptionValue[]) ?? [];

  const selectedCheckboxValues = R.map(
    (selectedCheckboxValueUncast) => (selectedCheckboxValueUncast as string | number).toString(),
    selectedCheckboxValuesUncast,
  );

  return selectedCheckboxValues;
};

export const onChange = (
  checkboxValue: string[],
  filter: Filter,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) =>
  onSelectFilterOption({
    filterUid: filter.uid,
    filterOptionValue: checkboxValue,
  });

type Props = Omit<FilterOptionsProps, 'currencyCode'>;

export const CheckboxGroupWrapper: FunctionComponent<Props> = ({
  filter,
  selectedFilterOptions,
  onSelectFilterOption,
  onExpandableContentToggle,
}: Props) => {
  const breakpoint = useBreakpoint();
  const isExpandable = filter.options.length > 5 && breakpoint !== Breakpoint.SMALL;

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable}
    >
      <CheckboxGroup
        name={filter.uid}
        accessibleLabel={filter.title}
        value={getSelectedCheckboxValues(selectedFilterOptions, filter.uid)}
        onChange={(checkboxValue: string[]) =>
          onChange(checkboxValue, filter, onSelectFilterOption)
        }
        checkboxData={filter.options.map(({ title, value }) => ({
          labelText: title,
          value: value as string,
        }))}
        hideLabel
      />
    </FilterOptionsExpandableContent>
  );
};
